import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/sharp-solid-svg-icons';
import type { FileHolderRef } from '@lib/api';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface DropdownFileItem {
  id: string;
  description: string;
  fileHolderId: string;
}

interface SCDropdownButtonProps {
  fileHolders?: FileHolderRef[];
  onDownload: (fileHolderId: string, fileId?: string) => void;
  /** When provided, each FileHolder is expanded into individual files via this fetch function.
   *  The dropdown will show individual file names instead of holder descriptions. */
  fetchFiles?: (fileHolderId: string) => Promise<{ id: string; name: string }[]>;
}

// Download dropdown for file holders — uses a portal so the popup
// is not clipped by table cell overflow.
function SCDropdownButton({ fileHolders, onDownload, fetchFiles }: SCDropdownButtonProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [resolvedFiles, setResolvedFiles] = useState<DropdownFileItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Fetch individual files eagerly when fetchFiles is provided
  useEffect(() => {
    if (!fetchFiles || fetchedRef.current || !fileHolders?.length) return;
    fetchedRef.current = true;

    let cancelled = false;
    setLoading(true);
    (async () => {
      const allFiles: DropdownFileItem[] = [];
      for (const fh of fileHolders) {
        if (!fh.id) continue;
        try {
          const files = await fetchFiles(fh.id);
          for (const f of files || []) {
            allFiles.push({
              id: f.id,
              description: f.name || fh.description || 'Output File',
              fileHolderId: fh.id,
            });
          }
        } catch {
          // If fetch fails for a holder, fall back to the holder itself
          allFiles.push({
            id: fh.id,
            description: fh.description || 'Output Files',
            fileHolderId: fh.id,
          });
        }
      }
      if (!cancelled) {
        setResolvedFiles(allFiles);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFiles, fileHolders]);

  if (!fileHolders?.length) return null;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 2, left: rect.right });
    }
    setOpen((prev) => !prev);
  };

  // When no fetchFiles and single file holder — simple download icon button
  if (!fetchFiles && fileHolders.length === 1) {
    return (
      <button
        title={`Download: ${fileHolders[0].description || 'Output Files'}`}
        onClick={(e) => {
          e.stopPropagation();
          if (fileHolders[0].id) onDownload(fileHolders[0].id);
        }}
        className="eone-ui-table-row-button"
      >
        <FontAwesomeIcon icon={faDownload} style={{ color: 'var(--link-color)' }} />
      </button>
    );
  }

  // Determine which items to show in the dropdown
  const items: { id: string; description: string; fileHolderId?: string }[] = fetchFiles
    ? resolvedFiles || []
    : fileHolders.map((fh) => ({ id: fh.id || '', description: fh.description || 'Output Files' }));

  // Single resolved file with fetchFiles — direct download
  if (fetchFiles && resolvedFiles?.length === 1) {
    const file = resolvedFiles[0];
    return (
      <button
        title={`Download: ${file.description}`}
        onClick={(e) => {
          e.stopPropagation();
          onDownload(file.fileHolderId, file.id);
        }}
        className="eone-ui-table-row-button"
      >
        <FontAwesomeIcon icon={faDownload} style={{ color: 'var(--link-color)' }} />
      </button>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        title="Download files"
        onClick={handleToggle}
        className="eone-ui-table-row-button"
      >
        <FontAwesomeIcon icon={faDownload} style={{ color: 'var(--link-color)' }} />
        {resolvedFiles !== null && <span style={{ fontSize: '10px', marginLeft: '2px' }}>▾</span>}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              transform: 'translateX(-100%)',
              zIndex: 10000,
              background: 'var(--control-background-color)',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              minWidth: '180px',
              padding: '4px 0',
            }}
          >
            {loading ? (
              <div style={{ padding: '6px 12px', fontSize: '13px', color: '#999' }}>Loading...</div>
            ) : (
              items.map((item) => (
                <div
                  key={`${item.fileHolderId || ''}-${item.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    if (fetchFiles && 'fileHolderId' in item) {
                      onDownload((item as DropdownFileItem).fileHolderId, item.id);
                    } else {
                      onDownload(item.id);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <FontAwesomeIcon
                    icon={faDownload}
                    style={{ marginRight: '8px', color: '#1890ff' }}
                  />
                  {item.description}
                </div>
              ))
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

export { SCDropdownButton };
